'use client'

import { useEffect, useState } from 'react'

import { faXmark } from '@document-crunch/pro-regular-svg-icons'

import { Icon, Spinner } from '@dc-shared-web/components/base'
import { Button, Dialog, DialogContent } from '@dc-shared-web/components/shadcn'
import SystemPromptRow from '@dc-shared-web/components/features/AIConfigDialog/SystemPromptRow'
import {
  AIConfigModalProvider,
  AIConfigModalValues,
} from '@dc-shared-web/components/features/AIConfigDialog/types'
import LanguageModelProviderRow from '@dc-shared-web/components/features/AIConfigDialog/LanguageModelProviderRow'
import LanguageModelRow from '@dc-shared-web/components/features/AIConfigDialog/LanguageModelRow'
import ContextModeRow from '@dc-shared-web/components/features/AIConfigDialog/ContextModeRow'
import ContextRow from '@dc-shared-web/components/features/AIConfigDialog/ContextRow'
import TemperatureRow from '@dc-shared-web/components/features/AIConfigDialog/TemperatureRow'
import TopPRow from '@dc-shared-web/components/features/AIConfigDialog/TopPRow'
import ProvisionsRow from '@dc-shared-web/components/features/AIConfigDialog/ProvisionsRow'
import { cn } from '@dc-shared-web/lib'
import DisplayLabelRow from '@dc-shared-web/components/features/AIConfigDialog/DisplayLabelRow'

// TODO: Implement provisions fetching
// TODO: Implement disabled prop on multiselect

export const AIConfigDialog = ({
  open,
  values: providedValues,
  defaultValues,
  providers,
  isLoading,
  context_mode_map,
  editable,
  onClose,
  onSubmit,
  className,
  isAgenticChat = false,
}: {
  open: boolean
  values: AIConfigModalValues
  defaultValues: AIConfigModalValues
  providers: AIConfigModalProvider[]
  isLoading?: boolean
  context_mode_map?: string[]
  editable?: boolean
  onClose: () => void
  onSubmit: (values: AIConfigModalValues) => void
  className?: string
  isAgenticChat?: boolean
}) => {
  const [values, setValues] = useState<AIConfigModalValues>(providedValues)

  const handleSubmit = () => {
    onSubmit(values)
    onClose()
  }

  const handleRestoreDefaults = () => {
    setValues(defaultValues)
  }

  useEffect(() => {
    setValues(providedValues)
  }, [providedValues])

  const useNewButton =
    typeof window !== 'undefined' && window.__VSS_UPDATES__?.buttonEnabled

  return (
    <Dialog open={open}>
      <DialogContent className={cn('md:tw-w-[520px]', className)}>
        <div className='tw-flex tw-content-between'>
          <h1 className='tw-font-medium tw-truncate tw-w-full tw-text-neutral-700 tw-text-base'>
            Settings
          </h1>
          <Button
            className={cn(
              'tw--mt-2 tw--mr-2',
              useNewButton ? undefined : 'tw-p-0'
            )}
            data-testid={`share-modal-close-btn`}
            variant='ghost'
            size={useNewButton ? undefined : 'icon'}
            onClick={onClose}
          >
            <Icon
              icon={faXmark}
              size='xl'
              className={useNewButton ? undefined : 'tw-text-neutral-400'}
            />
          </Button>
        </div>

        <div className='tw-flex tw-flex-col tw-gap-3 tw-relative'>
          {isLoading && (
            <div className='tw-absolute tw-inset-0 tw-bg-white/80 tw-flex tw-justify-center tw-items-center tw-z-10'>
              <Spinner className='tw-h-10 tw-w-10' />
            </div>
          )}

          <SystemPromptRow
            values={values}
            disabled={!editable}
            onChange={setValues}
          />
          <LanguageModelProviderRow
            values={values}
            disabled={!editable}
            providers={providers}
            onChange={setValues}
          />
          <LanguageModelRow
            values={values}
            disabled={!editable}
            providers={providers}
            onChange={setValues}
          />
          {values.context && !isAgenticChat && (
            <ContextRow value={values.context} />
          )}
          {!isAgenticChat && (
            <ContextModeRow
              values={values}
              disabled={!editable}
              mode_map={context_mode_map}
              onChange={setValues}
            />
          )}
          <TemperatureRow
            values={values}
            disabled={!editable}
            onChange={setValues}
          />
          <TopPRow values={values} disabled={!editable} onChange={setValues} />
          {!isAgenticChat && (
            <ProvisionsRow
              values={values}
              disabled={!editable}
              onChange={setValues}
            />
          )}
          <DisplayLabelRow
            values={values}
            disabled={!editable}
            onChange={setValues}
          />
        </div>

        {!editable ? (
          <div className='tw-flex tw-justify-end'>
            <Button onClick={onClose}>Close</Button>
          </div>
        ) : (
          <div className='tw-flex tw-justify-between'>
            <Button variant='gray' onClick={handleRestoreDefaults}>
              Revert to default
            </Button>
            <div className='tw-flex tw-gap-2'>
              <Button variant='gray' onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>Submit</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
